import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { Button, Card, Modal, Textarea, Toast } from '../../components/ui';
import { useT } from '../../i18n';
import { api, type BroadcastResult } from '../../lib/api';
import { haptic } from '../../lib/tma';

export function AdminBroadcastPage() {
  const t = useT();
  const [message, setMessage] = useState('');
  const [withButton, setWithButton] = useState(true);
  const [confirm, setConfirm] = useState(false);
  const [result, setResult] = useState<BroadcastResult | null>(null);

  const send = useMutation({
    mutationFn: () => api.adminBroadcast({ message: message.trim(), includeOpenButton: withButton }),
    onSuccess: (res) => {
      haptic('success');
      setResult(res);
      setMessage('');
    },
    onSettled: () => setConfirm(false),
  });

  const text = message.trim();
  const canSend = text.length > 0 && text.length <= 3000;

  return (
    <div className="min-h-screen p-4 max-w-lg mx-auto pb-12 safe-top safe-bottom">
      <PageHeader title={t.admin.broadcast} to="/admin" />

      <Card variant="surface" padding="md" className="space-y-3">
        <label className="block text-xs text-hint" htmlFor="broadcast-text">
          {t.admin.broadcastText}
        </label>
        <Textarea
          id="broadcast-text"
          value={message}
          onChange={(e) => {
            setMessage(e.target.value);
            setResult(null);
          }}
          rows={6}
          maxLength={3000}
        />
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={withButton}
            onChange={(e) => setWithButton(e.target.checked)}
          />
          {t.admin.broadcastButton}
        </label>
        <p className="text-xs text-hint">{t.admin.broadcastAudience}</p>
        <Button variant="ink" fullWidth disabled={!canSend} onClick={() => setConfirm(true)}>
          {t.admin.broadcastSend}
        </Button>
        {send.isError && <Toast tone="destructive">{t.common.failed}</Toast>}
        {result && (
          <Toast tone="success">
            {t.admin.broadcastDone(result.sent, result.blocked, result.failed)}
          </Toast>
        )}
      </Card>

      <Modal open={confirm} onClose={() => setConfirm(false)} title={t.admin.broadcastConfirm}>
        <p className="text-sm text-hint whitespace-pre-wrap break-words mb-4">{text}</p>
        <div className="grid grid-cols-2 gap-2">
          <Button variant="secondary" onClick={() => setConfirm(false)}>
            {t.common.back}
          </Button>
          <Button variant="ink" loading={send.isPending} onClick={() => send.mutate()}>
            {t.admin.broadcastSend}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
